/**
 * Conteneur pour le widget Elloha (réservation en ligne).
 *
 * Comme pour OpenSystem, on utilise un <iframe> pour que le script Elloha
 * (widget-loader.min.js / constellationWidgetLoad) trouve son conteneur via
 * getElementById dans un document dédié, compatible Shadow DOM.
 *
 * Documentation : https://doc.elloha.com/api/v1/fr/
 * Exemple : https://www.bruniquel.fr/tourisme/campings/#widget-details-offre-5824357
 */

import { html, css, PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import { ApidaeVisualHost } from "../ApidaeVisualHost";

const WIDGET_LOADER_URL =
  "https://reservation.elloha.com/Scripts/widget-loader.min.js";
const WIDGET_BASE_URL = "https://reservation.elloha.com";

@customElement("apidae-elloha-widget")
export class EllohaWidget extends ApidaeVisualHost {
  @property({ type: String })
  idBookingEngine: string = "";

  @property({ type: String })
  idProduct: string = "";

  @property({ type: String })
  culture: string = "fr-FR";

  @property({ type: String, attribute: "section-title" })
  sectionTitle: string = "Réservation en ligne";

  private setupKey = "";
  private resizeObserver: ResizeObserver | null = null;
  private heightPollTimer: ReturnType<typeof setTimeout> | null = null;

  static styles = css`
    :host {
      display: block;
    }
    iframe {
      width: 100%;
      border: none;
      display: block;
      min-height: 0;
      overflow: hidden;
      color-scheme: normal;
    }
  `;

  updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    this.trySetupIframe();
  }

  disconnectedCallback() {
    this.teardown();
    super.disconnectedCallback();
  }

  private teardown() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.heightPollTimer != null) {
      clearTimeout(this.heightPollTimer);
      this.heightPollTimer = null;
    }
  }

  private trySetupIframe() {
    if (!this.idBookingEngine || !this.idProduct) return;

    const key = `${this.idBookingEngine}|${this.idProduct}|${this.culture}`;
    if (key === this.setupKey) return;

    const iframe = this.renderRoot.querySelector(
      "iframe",
    ) as HTMLIFrameElement | null;
    if (!iframe) return;

    const doc = iframe.contentDocument;
    if (!doc) return;

    this.teardown();
    this.setupKey = key;

    // Elloha attend un id = ConstellationWidgetContainer + idBookingEngine (sans id produit).
    // L'id produit est passé dans l'URL (idoi). data-id-projet = idBookingEngine sans tirets.
    // Comme sur Bruniquel : définir constellationTypeModule{dataIdProjet} = 1 AVANT de charger
    // le script, sinon constellationWidgetSetAppearance() prend la branche else et plante (u undefined).
    const containerId = `ConstellationWidgetContainer${this.idBookingEngine}`;
    const dataIdProjet = this.idBookingEngine.replace(/-/g, "");
    const widgetUrl = `${WIDGET_BASE_URL}/Widget/BookingEngine/${this.idBookingEngine}?idoi=${encodeURIComponent(this.idProduct)}&culture=${encodeURIComponent(this.culture)}`;

    const safeContainerId = this.escAttr(containerId);
    const jsContainerId = this.escJs(containerId);
    const jsWidgetUrl = this.escJs(widgetUrl);
    const jsDataIdProjet = this.escJs(dataIdProjet);

    doc.open();
    doc.write(`<!DOCTYPE html>
<html><head><meta charset="utf-8">
<base href="${WIDGET_BASE_URL}/">
<style>html,body{margin:0;padding:0;overflow:visible;background:transparent;}</style>
</head><body>
<div id="${safeContainerId}" style="width:100%;text-align:center;" title="${this.escAttr(this.sectionTitle)}" data-id-projet="${this.escAttr(dataIdProjet)}">Chargement en cours...</div>
<script>
(function(){
  var containerId="${jsContainerId}";
  var widgetUrl="${jsWidgetUrl}";
  var dataIdProjet="${jsDataIdProjet}";
  window["constellationTypeModule"+dataIdProjet]=1;
  var container=document.getElementById(containerId);
  function runLoad(){
    if(typeof constellationWidgetLoad==="function"&&container){
      constellationWidgetLoad(containerId,widgetUrl);
      container.textContent="";
      if(typeof constellationWidgetAddEvent==="function"){
        constellationWidgetAddEvent(window,"resize",function(){
          if(typeof constellationWidgetSetAppearance==="function")
            constellationWidgetSetAppearance(containerId);
        });
      }
    }
  }
  if(typeof constellationWidgetLoad==="function"){ runLoad(); return; }
  var s=document.createElement("script");
  s.src="${WIDGET_LOADER_URL}";
  s.onload=runLoad;
  s.onerror=function(){ if(container) container.textContent="Chargement du widget de r\u00e9servation indisponible."; };
  document.head.appendChild(s);
})();
</script>
</body></html>`);
    doc.close();

    this.observeHeight(iframe);
  }

  private observeHeight(iframe: HTMLIFrameElement) {
    const sync = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;
        const body = doc.body;
        const docEl = doc.documentElement;
        let h = Math.max(body?.scrollHeight ?? 0, docEl?.scrollHeight ?? 0);
        const widgetRoot = doc.querySelector(
          "[id^='ConstellationWidgetContainer']",
        ) as HTMLElement | null;
        if (widgetRoot?.scrollHeight) {
          h = Math.max(h, widgetRoot.scrollHeight);
        }
        if (h > 0) iframe.style.height = `${h}px`;
      } catch {
        /* cross-origin guard */
      }
    };

    sync();

    try {
      const doc = iframe.contentDocument;
      const body = doc?.body;
      if (body) {
        this.resizeObserver = new ResizeObserver(sync);
        this.resizeObserver.observe(body);
        const widgetRoot = doc.querySelector(
          "[id^='ConstellationWidgetContainer']",
        );
        if (widgetRoot) this.resizeObserver.observe(widgetRoot);
      }
    } catch {
      /* fallback polling */
    }

    let ticks = 0;
    const poll = () => {
      sync();
      ticks++;
      if (ticks <= 60) this.heightPollTimer = setTimeout(poll, 500);
    };
    this.heightPollTimer = window.setTimeout(poll, 500);
  }

  private escAttr(s: string): string {
    return s
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  private escJs(s: string): string {
    return s
      .replace(/\\/g, "\\\\")
      .replace(/"/g, '\\"')
      .replace(/'/g, "\\'")
      .replace(/\n/g, "\\n")
      .replace(/\r/g, "\\r")
      .replace(/<\//g, "<\\/");
  }

  render() {
    if (!this.idBookingEngine || !this.idProduct) {
      return html``;
    }

    return html`
      <iframe title="${this.sectionTitle}" scrolling="no"></iframe>
    `;
  }
}
